import { queryOptions, useQuery } from '@tanstack/react-query';

import type { ClusterRestrictionDetails } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_RESTRICTIONS_DETAILS_QUERY_KEY = 'cluster-restrictions-details';

/** With params → exact key; without → base key for prefix invalidation. */
export const getRestrictionDetailsQueryKey = (params?: {
  clusterId: string;
  resourceName?: string;
  resourceKind?: string;
  resourceNamespace?: string;
}) =>
  params
    ? ([
        CLUSTER_RESTRICTIONS_DETAILS_QUERY_KEY,
        params.clusterId,
        params.resourceName,
        params.resourceKind,
        params.resourceNamespace,
      ] as const)
    : ([CLUSTER_RESTRICTIONS_DETAILS_QUERY_KEY] as const);

type RestrictionDetailsParams = {
  organizationId: string;
  clusterId: string;
  resourceName?: string;
  resourceKind?: string;
  resourceNamespace?: string;
};

const getRestrictionDetailsQueryOptions = (
  params: RestrictionDetailsParams & { isDemoMode?: boolean; enabled?: boolean }
) =>
  queryOptions<ClusterRestrictionDetails>({
    queryKey: getRestrictionDetailsQueryKey({
      clusterId: params.clusterId,
      resourceName: params.resourceName,
      resourceKind: params.resourceKind,
      resourceNamespace: params.resourceNamespace,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getClusterRestrictionDetails({
        organizationId: params.organizationId,
        clusterId: params.clusterId,
        resourceName: params.resourceName,
        resourceKind: params.resourceKind,
        resourceNamespace: params.resourceNamespace,
      });
      return data;
    },
    enabled: makeIsEnabled(
      params.enabled,
      !!params.organizationId,
      !!params.clusterId,
      !!params.resourceName,
      !!params.resourceKind,
      !!params.resourceNamespace,
      !params.isDemoMode
    ),
  });

type Props = RestrictionDetailsParams & {
  enabled?: boolean;
};

export const useRestrictionDetailsQuery = ({
  organizationId,
  clusterId,
  resourceName,
  resourceKind,
  resourceNamespace,
  enabled = true,
}: Props) => {
  const isDemoMode = useIsClusterDemoMode();

  return useQuery(
    getRestrictionDetailsQueryOptions({
      organizationId,
      clusterId,
      resourceName,
      resourceKind,
      resourceNamespace,
      enabled,
      isDemoMode,
    })
  );
};
